// The client side of /api/opportunities (BUILD-BRIEF §4).
import { request, qs } from './client.js';
import type {
  ListResponse, OpportunityCard, OpportunityDetail, OpportunityEvent, PipelineSummary, Stage,
} from '../../shared/types.js';

/**
 * The summary the server actually answers: `PipelineSummary` plus the two rail figures the
 * page needs that aren't in the shared shape (cycle time, win rate). Extending rather than
 * editing `shared/types.ts` — that file is the scaffold's contract, not the pipeline agent's.
 * `null` on any of the four extra fields means the trailing-12-month window has no deals to
 * compute from yet — the rail renders that as "not collected", never as a zero.
 */
export interface PipelineSummaryExt extends PipelineSummary {
  cycle_days_median: number | null;
  cycle_deals_12m: number;
  win_rate_12m: number | null;
  won_12m: number;
  lost_12m: number;
}

export function listOpportunities(params: { stage?: Stage; q?: string } = {}) {
  return request<ListResponse<OpportunityCard>>(`/opportunities${qs(params)}`);
}

export function getOpportunity(id: number) {
  return request<OpportunityDetail>(`/opportunities/${id}`);
}

export function getPipelineSummary() {
  return request<PipelineSummaryExt>('/opportunities/summary');
}

export interface NewOpportunityInput {
  organisation_id: number;
  title: string;
  face_value?: number | null;
  currency?: string;
  probability?: number | null;
  stage?: Stage;
  next_step?: string | null;
  next_step_due?: string | null;
  primary_person_id?: number | null;
}

export function createOpportunity(input: NewOpportunityInput) {
  return request<OpportunityCard>('/opportunities', { method: 'POST', body: JSON.stringify(input) });
}

export interface UpdateOpportunityInput {
  title?: string;
  face_value?: number | null;
  currency?: string;
  probability?: number | null;
  next_step?: string | null;
  next_step_due?: string | null;
  revisit_on?: string | null;
  primary_person_id?: number | null;
  signet_engagement_code?: string | null;
}

export function updateOpportunity(id: number, input: UpdateOpportunityInput) {
  return request<OpportunityCard>(`/opportunities/${id}`, { method: 'PATCH', body: JSON.stringify(input) });
}

export interface StageMoveInput {
  to_stage: Stage;
  reason?: string | null;
  quote?: string | null;
  revisit_on?: string | null;
}

export function moveStage(id: number, input: StageMoveInput) {
  return request<{ opportunity: OpportunityCard; events: OpportunityEvent[] }>(
    `/opportunities/${id}/stage`,
    { method: 'POST', body: JSON.stringify(input) },
  );
}

/** One event with the deal's ref and title folded in, for a cross-deal feed. */
export interface OpportunityEventView extends OpportunityEvent {
  opportunity_ref: string;
  opportunity_title: string;
  /** Days between this event and the previous one on the same deal; null for a deal's first event. */
  days_in_previous_stage: number | null;
}

export function listStageEvents(limit = 20) {
  return request<ListResponse<OpportunityEventView>>(`/opportunities/events${qs({ limit })}`);
}
